import { useTheme } from "@mui/material";
import { PositionStatusEnum } from "@sinch/core/entities/serverEnums";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { PositionStatusEnum as PositionStatus } from "@sinch/types/sinch.types";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

/**
 * Single source of truth for how a position status looks in the app: label, color and sort order.
 *
 * Use these getters wherever a position status is shown.
 * Never hardcode its label, color and sort order in a component, change them here.
 * Every value is shown in the "Entity Identity/PositionStatus" story.
 */
export const usePositionStatus = () => {
  const { t } = useTranslation();
  const { crewbossName } = useSettings();
  const { palette } = useTheme();

  const positionRoles = {
    [PositionStatusEnum.Pending]: {
      label: t("Shift.pending"),
      color: palette.entity.positionStatus[PositionStatusEnum.Pending],
      orderNumber: 0,
    },
    [PositionStatusEnum.Opened]: {
      label: t("Shift.open"),
      color: palette.entity.positionStatus[PositionStatusEnum.Opened],
      orderNumber: 1,
    },
    [PositionStatusEnum.OpenedHidden]: {
      label: t("Shift.openHidden"),
      color: palette.entity.positionStatus[PositionStatusEnum.OpenedHidden],
      orderNumber: 2,
    },
    [PositionStatusEnum.Running]: {
      label: t("Shift.running"),
      color: palette.entity.positionStatus[PositionStatusEnum.Running],
      orderNumber: 3,
    },
    [PositionStatusEnum.Over]: {
      label: t("Shift.finished"),
      color: palette.entity.positionStatus[PositionStatusEnum.Over],
      orderNumber: 4,
    },
    [PositionStatusEnum.CbCompleted]: {
      label: t("Shift.closedByStaffLeader", crewbossName),
      color: palette.entity.positionStatus[PositionStatusEnum.CbCompleted],
      orderNumber: 5,
    },
    [PositionStatusEnum.Completed]: {
      label: t("Shift.closedByAdmin"),
      color: palette.entity.positionStatus[PositionStatusEnum.Completed],
      orderNumber: 6,
    },
  } as Record<PositionStatusEnum | PositionStatus, { label: string; color: string; orderNumber: number }>;

  const getProp = useEntityProps(positionRoles);

  const getStatusLabel = getProp<string>("label");
  const getStatusColor = getProp<string>("color");
  const getStatusOrderNumber = getProp<number>("orderNumber");

  return { getStatusLabel, getStatusColor, getStatusOrderNumber };
};
